<script setup lang="ts">
  import { computed, watch, ref } from "vue";
  import MarketoInputSyncWrapper from "./marketo-input-sync-wrapper.vue";
  import type { MarketoUserInputMeta } from "../types/definitions/inputs/marketo-input-meta.js";
  import type { InputState } from "../composables/use-input-state.js";
  import { TranslationComponent } from "i18next-vue";
  import { FormLabelKeysEnum } from "../locale/translation-keys/all-keys.js";
  import { TranslationNamespaceEnum } from "../locale/settings/translation-namespaces.js";
  import { usePrivacyConsentState  } from "../composables/use-privacy-consent-state.js";
  import privacyStatement from "./privacy-statement.vue";
  import { refDefault, useVModel } from "@vueuse/core";
  import type { Nil } from "@mcwd/typescript-type-guards";
  import { useSiteLanguage } from '../composables/use-site-language.js';
  import { useTranslation } from "../composables/use-translation.js";
  
  export interface EmailOptInCheckBoxProps {
    formInputMeta: MarketoUserInputMeta<"Email_Opt_In__c">;
    inputState: InputState;
    modelValue: boolean | Nil;
  }
  
  const { localeUrlPrefix } = useSiteLanguage({ callerLabel: "email-opt-in-checkbox" });
  const props = defineProps<EmailOptInCheckBoxProps>();

  const emit = defineEmits<(e: 'update:modelValue', modelValue: EmailOptInCheckBoxProps["modelValue"]) => void>();

  const proxyModelValue = refDefault(useVModel(props, 'modelValue', emit), null);

  const { translate } = useTranslation();

  const parentWrapperClasses = computed(() => {
    return [{ error: props.inputState.showError, done: props.inputState.done, required: props.inputState.required }];
  });

  const { privacyConsentState } = usePrivacyConsentState({ callerLabel: "email-opt-in-checkbox" });

  const consentRequired = privacyConsentState.consentRequired;

  const consentCheckboxUserValue = ref(false);

  // Set initial value
  proxyModelValue.value = consentRequired.value === false; // true if consent is not required

  watch(() => consentRequired.value, (currentlyRequired: boolean, prevRequired: boolean) => {
    // Switch from consent not required to required
    if (prevRequired === false && currentlyRequired === true) {
      // Restore user value
      proxyModelValue.value = consentCheckboxUserValue.value;
    }
    // Switch from consent required to not required
    else if (prevRequired === true && currentlyRequired === false) {
      // Save user value
      consentCheckboxUserValue.value = (proxyModelValue.value === true);

      // Set value to true when consent is not required
      proxyModelValue.value = true;
    }
  });
</script>

<template>
  <MarketoInputSyncWrapper v-model="proxyModelValue" :marketo-input-name="formInputMeta.marketoName">
    <template v-if="consentRequired">
      <div :id="'parent_' + formInputMeta.marketoName" class="parent-field-wrpr " :data-type="formInputMeta.type" :class="parentWrapperClasses">
        <label class="eclipse-label" :for="formInputMeta.name">
          <p>
            <TranslationComponent :translation="translate(FormLabelKeysEnum.eclipse_Email_Opt_In__c, { ns: TranslationNamespaceEnum.formLabels })">
              <template #privacy-policy-link-slot>
                <a class="privacy-href" :href="`${localeUrlPrefix}/privacy/`" target="_blank">
                  {{ translate(FormLabelKeysEnum.privacyPolicyLinkText, { ns: TranslationNamespaceEnum.formLabels }) }}
                </a>
              </template>
            </TranslationComponent>
            <input :id="formInputMeta.name" v-model="proxyModelValue" :type="formInputMeta.type" :name="formInputMeta.name" :data-active-element-match-name="formInputMeta.marketoName">
            <span class="checkmark"></span>
          </p>
        </label>
        <div class="error-msg">
          <slot name="error">
            translated error here
          </slot>
        </div>
      </div>
    </template>
    <template v-else>
      <privacyStatement />
    </template>
  </MarketoInputSyncWrapper>
</template>